/**
 * Canvas 2D trend charts (CLAUDE.md stack: SVG for scaled graphics, Canvas for trends, no
 * charting libraries).
 *
 * One generic renderer covers every trend in the app: series, optional shaded band (the forecast
 * fan), threshold lines, and an optional "now" marker separating history from projection.
 */

import { useEffect, useRef } from 'react';

export interface Series {
  x: number[];
  y: number[];
  colour: string;
  width?: number;
  dash?: number[];
  label?: string;
  /** Fill down to the axis. */
  fill?: string;
}

export interface Band {
  x: number[];
  lo: number[];
  hi: number[];
  colour: string;
  label?: string;
}

export interface Threshold {
  value: number;
  colour: string;
  label: string;
  dash?: number[];
}

export interface TrendProps {
  series: Series[];
  bands?: Band[];
  thresholds?: Threshold[];
  height?: number;
  yLabel?: string;
  xLabel?: string;
  /** Force a y range; otherwise it is fitted with 6 % headroom. */
  yMin?: number;
  yMax?: number;
  /** Vertical marker, e.g. "now". */
  marker?: { x: number; label: string } | null;
  /** Format for the y axis ticks. */
  fmt?(v: number): string;
}

const PAD_L = 52;
const PAD_R = 12;
const PAD_T = 10;
const PAD_B = 22;

export function Trend({
  series,
  bands = [],
  thresholds = [],
  height = 180,
  yLabel,
  xLabel,
  yMin,
  yMax,
  marker = null,
  fmt = (v) => (Math.abs(v) >= 1000 ? Math.round(v).toLocaleString('en-US') : v.toFixed(1)),
}: TrendProps): JSX.Element {
  const ref = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canvas = ref.current;
    if (!canvas) return;
    const parent = canvas.parentElement;
    const cssW = parent ? parent.clientWidth : 400;
    const dpr = window.devicePixelRatio || 1;
    canvas.width = Math.max(1, Math.floor(cssW * dpr));
    canvas.height = Math.floor(height * dpr);
    canvas.style.width = `${cssW}px`;
    canvas.style.height = `${height}px`;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.clearRect(0, 0, cssW, height);

    // Fit the domain across every series, band and threshold.
    let x0 = Infinity;
    let x1 = -Infinity;
    let y0 = Infinity;
    let y1 = -Infinity;
    const consider = (xs: number[], ys: number[]): void => {
      for (let i = 0; i < xs.length; i++) {
        const xv = xs[i]!;
        const yv = ys[i]!;
        if (!Number.isFinite(xv) || !Number.isFinite(yv)) continue;
        if (xv < x0) x0 = xv;
        if (xv > x1) x1 = xv;
        if (yv < y0) y0 = yv;
        if (yv > y1) y1 = yv;
      }
    };
    for (const s of series) consider(s.x, s.y);
    for (const b of bands) {
      consider(b.x, b.lo);
      consider(b.x, b.hi);
    }
    // Thresholds expand the range only when they are *near* the data. A 1,250 MW export limit
    // against a 357 MW trace would otherwise squash the trace into the bottom eighth of the plot
    // and make the thing it is meant to show unreadable. Far-off thresholds are simply not drawn
    // (the draw loop already skips anything outside the plot area).
    const dataSpan = Math.max(1e-9, y1 - y0);
    for (const t of thresholds) {
      if (t.value < y0 && t.value > y0 - dataSpan * 0.5) y0 = t.value;
      if (t.value > y1 && t.value < y1 + dataSpan * 0.5) y1 = t.value;
    }
    if (!Number.isFinite(x0) || !Number.isFinite(y0)) return;
    if (x1 === x0) x1 = x0 + 1;
    if (yMin !== undefined) y0 = yMin;
    if (yMax !== undefined) y1 = yMax;
    const span = y1 - y0 || 1;
    if (yMin === undefined) y0 -= span * 0.06;
    if (yMax === undefined) y1 += span * 0.06;

    const plotW = cssW - PAD_L - PAD_R;
    const plotH = height - PAD_T - PAD_B;
    const sx = (v: number): number => PAD_L + ((v - x0) / (x1 - x0)) * plotW;
    const sy = (v: number): number => PAD_T + plotH - ((v - y0) / (y1 - y0)) * plotH;

    // Grid and y ticks.
    ctx.font = '10px ui-sans-serif, system-ui, sans-serif';
    ctx.textBaseline = 'middle';
    ctx.strokeStyle = '#232a30';
    ctx.fillStyle = '#8b959d';
    ctx.lineWidth = 1;
    for (let i = 0; i <= 4; i++) {
      const v = y0 + ((y1 - y0) * i) / 4;
      const yy = Math.round(sy(v)) + 0.5;
      ctx.beginPath();
      ctx.moveTo(PAD_L, yy);
      ctx.lineTo(cssW - PAD_R, yy);
      ctx.stroke();
      ctx.textAlign = 'right';
      ctx.fillText(fmt(v), PAD_L - 6, yy);
    }

    // Bands beneath everything.
    for (const b of bands) {
      ctx.fillStyle = b.colour;
      ctx.beginPath();
      for (let i = 0; i < b.x.length; i++) ctx.lineTo(sx(b.x[i]!), sy(b.hi[i]!));
      for (let i = b.x.length - 1; i >= 0; i--) ctx.lineTo(sx(b.x[i]!), sy(b.lo[i]!));
      ctx.closePath();
      ctx.fill();
    }

    // Threshold lines, each labelled — colour is never the only carrier of state.
    for (const t of thresholds) {
      const yy = Math.round(sy(t.value)) + 0.5;
      if (yy < PAD_T || yy > PAD_T + plotH) continue;
      ctx.save();
      ctx.strokeStyle = t.colour;
      ctx.setLineDash(t.dash ?? [3, 3]);
      ctx.beginPath();
      ctx.moveTo(PAD_L, yy);
      ctx.lineTo(cssW - PAD_R, yy);
      ctx.stroke();
      ctx.restore();
      ctx.fillStyle = t.colour;
      ctx.textAlign = 'left';
      ctx.fillText(t.label, PAD_L + 4, yy - 7);
    }

    // Series.
    for (const s of series) {
      if (s.x.length === 0) continue;
      if (s.fill) {
        ctx.fillStyle = s.fill;
        ctx.beginPath();
        ctx.moveTo(sx(s.x[0]!), sy(y0));
        for (let i = 0; i < s.x.length; i++) ctx.lineTo(sx(s.x[i]!), sy(s.y[i]!));
        ctx.lineTo(sx(s.x[s.x.length - 1]!), sy(y0));
        ctx.closePath();
        ctx.fill();
      }
      ctx.save();
      ctx.strokeStyle = s.colour;
      ctx.lineWidth = s.width ?? 1.5;
      ctx.setLineDash(s.dash ?? []);
      ctx.beginPath();
      for (let i = 0; i < s.x.length; i++) {
        const px = sx(s.x[i]!);
        const py = sy(s.y[i]!);
        if (i === 0) ctx.moveTo(px, py);
        else ctx.lineTo(px, py);
      }
      ctx.stroke();
      ctx.restore();
    }

    // "Now" marker.
    if (marker) {
      const mx = Math.round(sx(marker.x)) + 0.5;
      ctx.strokeStyle = '#dfe4e8';
      ctx.globalAlpha = 0.5;
      ctx.beginPath();
      ctx.moveTo(mx, PAD_T);
      ctx.lineTo(mx, PAD_T + plotH);
      ctx.stroke();
      ctx.globalAlpha = 1;
      ctx.fillStyle = '#8b959d';
      ctx.textAlign = 'left';
      ctx.fillText(marker.label, mx + 4, PAD_T + 8);
    }

    // Axis frame and labels.
    ctx.strokeStyle = '#2b3239';
    ctx.beginPath();
    ctx.moveTo(PAD_L, PAD_T);
    ctx.lineTo(PAD_L, PAD_T + plotH);
    ctx.lineTo(cssW - PAD_R, PAD_T + plotH);
    ctx.stroke();

    ctx.fillStyle = '#8b959d';
    if (yLabel) {
      ctx.textAlign = 'left';
      ctx.fillText(yLabel, PAD_L + 2, PAD_T + 6);
    }
    if (xLabel) {
      ctx.textAlign = 'right';
      ctx.fillText(xLabel, cssW - PAD_R, height - 8);
    }

    // Legend.
    let lx = PAD_L + 4;
    const ly = height - 8;
    ctx.textAlign = 'left';
    for (const s of series) {
      if (!s.label) continue;
      ctx.strokeStyle = s.colour;
      ctx.lineWidth = 2;
      ctx.setLineDash(s.dash ?? []);
      ctx.beginPath();
      ctx.moveTo(lx, ly);
      ctx.lineTo(lx + 14, ly);
      ctx.stroke();
      ctx.setLineDash([]);
      ctx.fillStyle = '#8b959d';
      ctx.fillText(s.label, lx + 18, ly);
      lx += 26 + ctx.measureText(s.label).width;
    }
  });

  return (
    <div style={{ width: '100%', height }}>
      <canvas ref={ref} role="img" aria-label={yLabel ?? 'trend chart'} />
    </div>
  );
}
